(function () {
  if (!window.customElements) return;
  if (customElements.get('variant-image-dropdown')) return;

  class VariantImageDropdown extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.toggle = this.querySelector('[data-variant-image-toggle]');
      this.panel = this.querySelector('[data-variant-image-panel]');
      this.select = this.querySelector('select');
      this.options = Array.from(this.querySelectorAll('[data-button]'));
      this.closeButtons = Array.from(this.querySelectorAll('[data-variant-image-close]'));
      this.selectedImage = this.querySelector('[data-variant-image-selected-image]');
      this.selectedLabel = this.querySelector('[data-variant-image-selected-label]');
      this.quickViewViewport = this.closest('.quick-product--right-viewport');

      if (!this.toggle || !this.panel || !this.select || !this.options.length) return;

      this.handleToggle = this.handleToggle.bind(this);
      this.handleSelectChange = this.handleSelectChange.bind(this);
      this.handleOptionClick = this.handleOptionClick.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleAnotherDropdownOpen = this.handleAnotherDropdownOpen.bind(this);

      this.toggle.addEventListener('click', this.handleToggle);
      this.select.addEventListener('change', this.handleSelectChange);
      this.options.forEach((option) => option.addEventListener('click', this.handleOptionClick));
      this.closeButtons.forEach((button) => button.addEventListener('click', this.handleCloseClick));
      this.addEventListener('keydown', this.handleKeyDown);
      document.addEventListener('pointerdown', this.handleDocumentPointerDown);
      document.addEventListener('variant-image-dropdown:open', this.handleAnotherDropdownOpen);

      this.syncFromSelect();
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.toggle?.removeEventListener('click', this.handleToggle);
      this.select?.removeEventListener('change', this.handleSelectChange);
      this.options?.forEach((option) => option.removeEventListener('click', this.handleOptionClick));
      this.closeButtons?.forEach((button) => button.removeEventListener('click', this.handleCloseClick));
      this.removeEventListener('keydown', this.handleKeyDown);
      document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
      document.removeEventListener('variant-image-dropdown:open', this.handleAnotherDropdownOpen);
      this.quickViewViewport?.classList.remove('variant-image-dropdown-active');
      this.releaseScrollLock();
      this.initialized = false;
    }

    handleToggle() {
      this.classList.contains('is-open') ? this.close() : this.open();
    }

    handleSelectChange() {
      window.requestAnimationFrame(() => {
        this.syncFromSelect();
        this.close();
      });
    }

    handleOptionClick() {
      // The theme's existing OptionButtons controller updates the native select and dispatches
      // its change event. Waiting one frame keeps that controller as the single source of truth.
      window.requestAnimationFrame(() => {
        this.syncFromSelect();
        this.close();
      });
    }

    handleCloseClick() {
      this.close(true);
    }

    handleDocumentPointerDown(event) {
      if (this.classList.contains('is-open') && !this.contains(event.target)) {
        this.close();
      }
    }

    handleAnotherDropdownOpen(event) {
      if (event.detail !== this) this.close();
    }

    handleKeyDown(event) {
      if (event.key === 'Escape' && this.classList.contains('is-open')) {
        event.preventDefault();
        this.close(true);
        return;
      }

      if (event.target === this.toggle && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        this.open();
        this.focusOption(event.key === 'ArrowUp' ? this.options.length - 1 : this.selectedOptionIndex());
        return;
      }

      if (!this.options.includes(event.target)) return;

      const currentIndex = this.options.indexOf(event.target);
      let nextIndex;

      switch (event.key) {
        case 'ArrowDown':
          nextIndex = (currentIndex + 1) % this.options.length;
          break;
        case 'ArrowUp':
          nextIndex = (currentIndex - 1 + this.options.length) % this.options.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = this.options.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.focusOption(nextIndex);
    }

    open() {
      if (this.classList.contains('is-open')) return;
      this.syncFromSelect();
      document.dispatchEvent(new CustomEvent('variant-image-dropdown:open', { detail: this }));
      this.classList.add('is-open');
      this.quickViewViewport?.classList.add('variant-image-dropdown-active');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.panel.setAttribute('aria-hidden', 'false');

      if (window.matchMedia('(max-width: 749px)').matches) {
        document.documentElement.classList.add('variant-image-dropdown-open');
      }
    }

    close(returnFocus) {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.quickViewViewport?.classList.remove('variant-image-dropdown-active');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.panel.setAttribute('aria-hidden', 'true');
      this.releaseScrollLock();
      if (returnFocus) this.toggle.focus();
    }

    releaseScrollLock() {
      if (!document.querySelector('variant-image-dropdown.is-open')) {
        document.documentElement.classList.remove('variant-image-dropdown-open');
      }
    }

    selectedOptionIndex() {
      const index = this.options.findIndex((option) => option.dataset.optionValue === this.select.value);
      return index < 0 ? 0 : index;
    }

    focusOption(index) {
      this.options[index]?.focus();
    }

    syncFromSelect() {
      const selectedOption = this.options.find((option) => option.dataset.optionValue === this.select.value);
      if (!selectedOption) return;

      this.options.forEach((option) => {
        const isSelected = option === selectedOption;
        option.classList.toggle('selected', isSelected);
        option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      });

      const optionLabel = selectedOption.querySelector('.variant-image-dropdown__option-label');
      if (optionLabel) this.selectedLabel.innerHTML = optionLabel.innerHTML;
      this.toggle.classList.toggle('is-unavailable', selectedOption.classList.contains('disabled'));

      const optionImage = selectedOption.querySelector('.variant-image-dropdown__option-image');
      if (optionImage && this.selectedImage) {
        this.selectedImage.innerHTML = optionImage.innerHTML;
        const image = this.selectedImage.querySelector('img');
        if (image) image.loading = 'eager';
      }
    }
  }

  customElements.define('variant-image-dropdown', VariantImageDropdown);
})();
